package com.far.suller.sections

import androidx.compose.runtime.*
import com.far.suller.components.sectionTitle
import com.far.suller.models.Section
import com.far.suller.util.Constants
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

private val AMBER_500 = Color.rgb(245, 158, 11)
private val DARK_BROWN = Color.rgb(44, 25, 16)

data class ServiceSpiel(
    val icon: String,
    val title: String,
    val subtitle: String,
    val description: String,
)

val serviceSpiels = listOf(
    ServiceSpiel(
        icon = "/ic_android_hybrid_engineering.svg",
        title = "Android & Hybrid Engineering",
        subtitle = "Native, Vue, and Svelte",
        description = "Expert in native Android development with over 8 years of experience. Pioneering hybrid solutions by integrating Vue and Svelte web components into native apps via WebView to deliver high-performance, cross-functional mobile experiences.",
    ),
    ServiceSpiel(
        icon = "/ic_backend_core_services.svg",
        title = "Backend & Core Services",
        subtitle = "Spring Boot, Ktor, PostgreSQL",
        description = "Building scalable backend architectures using modern Java and Kotlin frameworks. Proficient in API design (Swagger/OpenAPI), JPA/Hibernate, and infrastructure tools like Docker, Redis, and PostgreSQL for robust system ecosystems.",
    ),
    ServiceSpiel(
        icon = "/ic_ai_assisted_development.svg",
        title = "AI-Assisted Development",
        subtitle = "Productivity & Code Quality",
        description = "Leveraging cutting-edge AI tools to accelerate prototyping, code generation, and refactoring. Focused on improving maintainability and ensuring architectural standards are met while driving rapid feature delivery.",
    ),
    ServiceSpiel(
        icon = "/ic_security_testing.svg",
        title = "Security & Testing",
        subtitle = "VAPT, Unit & Integration",
        description = "Implementation of security best practices, including VAPT requirements and RSA encryption. Ensuring system stability through rigorous automated unit and integration testing using tools like Testcontainers and JUnit.",
    ),
    ServiceSpiel(
        icon = "/ic_agile_cross_functional.svg",
        title = "Agile & Cross-Functional",
        subtitle = "Agile, Scrum, Leadership",
        description = "Extensive experience in daily stand-ups, sprint planning, and refinements. Collaborating closely with product owners and stakeholders to align technical implementations with strategic business goals.",
    ),
    ServiceSpiel(
        icon = "/ic_deployment_infrastructure.svg",
        title = "Deployment & Infrastructure",
        subtitle = "CI/CD, Azure, Linux",
        description = "Managing non-production and staging environments to ensure stable integrations. Expertise in CI/CD pipelines, Azure DevOps, and Linux-based flashing/testing environments for end-to-end lifecycle management.",
    ),
)

@Composable
fun serviceSectionV2() {
    val breakpoint = rememberBreakpoint()
    val isDesktop = breakpoint >= Breakpoint.MD

    Box(
        modifier = Modifier
            .id(Section.Service.id)
            .fillMaxWidth()
            .maxWidth(Constants.SECTION_WIDTH.px)
            .padding(topBottom = if (isDesktop) 150.px else 80.px)
            .backgroundColor(DARK_BROWN),
        contentAlignment = Alignment.Center,
    ) {
        Column(
            modifier = Modifier.fillMaxWidth(if (isDesktop) 90.percent else 95.percent),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            sectionTitle(
                modifier = Modifier.margin(bottom = if (isDesktop) 100.px else 60.px),
                section = Section.Service,
                alignment = Alignment.CenterHorizontally,
            )

            SimpleGrid(
                modifier = Modifier.fillMaxWidth(),
                numColumns = numColumns(base = 1, md = 2, lg = 3),
            ) {
                serviceSpiels.forEach { spiel ->
                    ServiceSpielCard(spiel)
                }
            }
        }
    }
}

@Composable
private fun ServiceSpielCard(spiel: ServiceSpiel) {
    var isHovered by remember { mutableStateOf(false) }

    Box(
        modifier = Modifier
            .padding(all = 16.px)
            .fillMaxSize(),
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(all = 32.px)
                .backgroundColor(if (isHovered) Color.rgba(245, 158, 11, 0.05f) else Color.rgba(255, 255, 255, 0.02f))
                .border(
                    width = 1.px,
                    style = LineStyle.Solid,
                    color = if (isHovered) Color.rgba(245, 158, 11, 0.3f) else Color.rgba(255, 255, 255, 0.08f)
                )
                .borderRadius(24.px)
                .onMouseEnter { isHovered = true }
                .onMouseLeave { isHovered = false }
                .styleModifier {
                    property("box-shadow", if (isHovered) "0 30px 60px rgba(245, 158, 11, 0.2)" else "0 20px 40px rgba(0,0,0,0.3)")
                    property("transform", if (isHovered) "scale(1.05) translateY(-10px)" else "scale(1) translateY(0)")
                    property("transition", "all 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)")
                    property("z-index", if (isHovered) "10" else "1")
                    property("position", "relative")
                },
        ) {
            Image(
                src = spiel.icon,
                modifier = Modifier
                    .size(48.px)
                    .margin(bottom = 24.px)
                    .styleModifier {
                        if (isHovered) {
                            // Turn the SVG icon into vibrant amber with drop shadow
                            property("filter", "invert(62%) sepia(85%) saturate(1450%) hue-rotate(350deg) brightness(101%) contrast(97%) drop-shadow(0 0 12px rgba(245, 158, 11, 0.8))")
                        } else {
                            // Invert black SVG lines to white to fit on dark theme
                            property("filter", "invert(1)")
                        }
                        property("transition", "all 0.3s ease")
                    }
            )

            P(
                attrs = Modifier
                    .margin(top = 0.px, bottom = 8.px)
                    .fontFamily(Constants.FONT_FAMILY)
                    .fontSize(20.px)
                    .fontWeight(FontWeight.Bold)
                    .color(Colors.White)
                    .toAttrs(),
            ) { Text(spiel.title) }

            P(
                attrs = Modifier
                    .margin(top = 0.px, bottom = 20.px)
                    .fontFamily(Constants.FONT_FAMILY)
                    .fontSize(14.px)
                    .fontWeight(FontWeight.Medium)
                    .color(AMBER_500)
                    .toAttrs(),
            ) { Text(spiel.subtitle) }

            P(
                attrs = Modifier
                    .margin(top = 0.px, bottom = 0.px)
                    .fontFamily(Constants.FONT_FAMILY)
                    .fontSize(15.px)
                    .color(Color.rgba(255, 255, 255, 0.7f))
                    .lineHeight(1.6)
                    .toAttrs(),
            ) { Text(spiel.description) }
        }
    }
}
